import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { GhostIcon, PencilIcon, SendIcon, TrophyIcon } from "lucide-react";
import { useMessagesStore } from "stores/messages";

import type { MessageThreadWithParticipants } from "simplydo/interfaces/types/messageTypes";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import {
  EntityToolbar,
  type ToolbarItem,
  ToolbarItems,
} from "components/lib/EntityHeader";
import { RoleLabel } from "components/lib/Roles/RoleLabel";
import type { PublicUser } from "components/users/types";

type UserToolbarProps = {
  profileUser: PublicUser;
  onAwardAchievement: () => void;
};

const UserToolbar = ({ profileUser, onAwardAchievement }: UserToolbarProps) => {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const navigate = useNavigate();
  const onMessageThreadSelected = useMessagesStore(
    (state) => state.selectThread,
  );

  const isYourProfile = profileUser._id === user?._id;

  const messageUser = () => {
    api.messages.findThreadByUser(
      profileUser._id,
      (data) => {
        const newThread = {
          _id: `newThread:${profileUser._id}`,
          createdAt: new Date().toISOString(),
          participants: [profileUser._id],
          participantUsers: [profileUser],
        } as MessageThreadWithParticipants;
        onMessageThreadSelected(
          (data?.thread as MessageThreadWithParticipants | undefined) ??
            newThread,
        );
      },
      (err) => toast.error(err.message),
    );
  };

  const ghost = () => {
    util
      .confirm(
        t("users.chip.ghostConfirmTitle"),
        t("users.chip.ghostConfirmText"),
      )
      .then(() =>
        api.users.ghost(
          profileUser._id,
          () => {
            api.invalidateQueries();
            navigate("/");
          },
          (err) => toast.error(err.message),
        ),
      )
      .catch(() => {});
  };

  const items: ToolbarItem[] = [];
  if (isYourProfile) {
    items.push({
      key: "edit",
      group: "profile",
      tier: "pinned",
      label: t("users.preferences.profile.edit"),
      icon: PencilIcon,
      to: "/preferences/profile",
    });
  } else {
    if (
      user?.ownerOrganisation?.achievementsEnabled !== false &&
      (profileUser.customAchievements?.length ?? 0) > 0 &&
      (util.hasPermission(user, "org.viewDashboard") ||
        util.hasPermission(user, "super.viewDashboard"))
    ) {
      items.push({
        key: "award",
        group: "admin",
        tier: "default",
        label: t("users.profile.awardAchievement"),
        icon: TrophyIcon,
        onClick: onAwardAchievement,
      });
    }
    if (
      util.hasPermission(
        user,
        "super.ghostUser",
        profileUser.ownerOrganisation?._id,
      )
    ) {
      items.push({
        key: "ghost",
        group: "admin",
        tier: "low",
        label: t("users.profile.ghost"),
        icon: GhostIcon,
        onClick: ghost,
      });
    }
    if (
      profileUser.canBeMessagedByCurrentUser &&
      user?.ownerOrganisation?.enabledFeatures?.includes("directMessaging")
    ) {
      items.push({
        key: "message",
        group: "profile",
        tier: "pinned",
        label: t("users.chip.message"),
        icon: SendIcon,
        variant: "regular",
        color: "primary",
        onClick: messageUser,
      });
    }
  }

  return (
    <EntityToolbar
      start={
        profileUser.iconRoles?.length ? (
          <div style={{ display: "flex", gap: 6 }}>
            {profileUser.iconRoles.map((role) => (
              <RoleLabel key={`${role.name}-${role.icon.name}`} role={role} />
            ))}
          </div>
        ) : null
      }
      end={<ToolbarItems items={items} />}
    />
  );
};

export default UserToolbar;
